/**
 * @module makeClientConfigurationRequest
 */

import { getUserMailboxId } from 'owa-client-ids';
import { getClientVersion } from 'owa-actionable-message-analytics';
import { getClientAuthToken } from 'owa-connectors';
import { getCorrelationVector } from 'owa-service/lib/correlationVector';
import {
    CLIENT_CONFIGURATION_API_VERSION as apiVersion,
    CLIENT_NAME as clientType,
    CLIENT_DEFAULT_THEME as defaultColorScheme,
} from '../constants';
import { getActionEndpointUrlFromStore } from '../selectors/getActionEndpoint';

/**
 * @function
 * To construct the request URL for the client configuration API
 * @returns The constructed url string
 */

const getRequestURL = (): string => {
    const mailBoxId: string = getUserMailboxId();
    const clientVersion: string = getClientVersion();
    return `${getActionEndpointUrlFromStore()}/${apiVersion}/${mailBoxId}/clientConfigs/${clientType}?clientVersion=${clientVersion}&clientColorScheme=${defaultColorScheme}&$expand=providerConfigs`;
};

/**
 * @function
 * To create Bearer token from the client auth token
 * @param {string} authToken - Token string from which the Bearer token needs to be constructed
 * @returns The constructed Bearer token
 */

const buildBearerToken = (authToken: string): string => {
    return `Bearer ${authToken}`;
};

/**
 * @async
 * @function
 * To construct the headers for the client configuration API
 * @returns A promise that resolves to Headers Object
 */

const getHeaders = async (): Promise<Headers> => {
    const headers: Headers = new Headers();
    const authToken: string = await getClientAuthToken();
    headers.set('Authorization', buildBearerToken(authToken));
    headers.set('MS-CV', getCorrelationVector());
    return headers;
};

/**
 * @async
 * @function
 * To make a fetch request to get AM client configurations
 * @returns The configuration data
 */

const makeClientConfigurationRequest = async (): Promise<any> => {
    try {
        const url: string = getRequestURL();
        const headers: Headers = await getHeaders();
        const request: Request = new Request(url, {
            method: 'GET',
            headers,
        });
        const response = await fetch(request);
        if (response.ok) {
            // parse only in case of 2XX response
            // disabling tslint rule as we need to await here
            // for the catch block to work properly
            // https://github.com/palantir/tslint/issues/3933
            // tslint:disable-next-line:no-return-await
            return await response.json();
        } else {
            throw response;
        }
    } catch (error) {
        throw error;
    }
};

export default makeClientConfigurationRequest;
